import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useApp } from '@/store/AppContext'
import { calculatePrice, formatCurrency, formatPlate } from '@/lib/pricing'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import {
  Search,
  Banknote,
  CreditCard,
  Smartphone,
  ArrowLeft,
  CheckCircle2,
} from 'lucide-react'
import { differenceInMinutes } from 'date-fns'

export default function Checkout() {
  const [params] = useSearchParams()
  const { tickets, rules, checkOut, settings, currentSession } = useApp()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [search, setSearch] = useState('')
  const [ticketId, setTicketId] = useState<string | null>(params.get('id'))

  const ticket = tickets.find((t) => t.id === ticketId)

  useEffect(() => {
    if (!ticketId && search.length >= 7) {
      const found = tickets.find(
        (t) => t.plate === formatPlate(search) && t.status === 'active',
      )
      if (found) setTicketId(found.id)
    }
  }, [search, ticketId, tickets])

  if (currentSession?.status !== 'open') {
    return (
      <div className="max-w-md mx-auto mt-10 animate-fade-in-up">
        <Card className="shadow-elevation border-none md:border-solid">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-destructive">
              Caixa Fechado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-center">
            <p className="text-muted-foreground">
              O caixa precisa estar aberto para registrar saídas e receber
              pagamentos no sistema.
            </p>
            <Button
              onClick={() => navigate('/cashier')}
              className="w-full h-14 text-lg font-bold"
            >
              Ir para o Caixa
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!ticketId || !ticket) {
    return (
      <div className="max-w-md mx-auto mt-10 animate-fade-in-up">
        <Card className="shadow-elevation border-none md:border-solid">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              Finalizar Saída
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-center text-muted-foreground">
              Digite a placa do veículo para calcular o valor e registrar a
              saída.
            </p>
            <form onSubmit={(e) => e.preventDefault()} className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="ABC-1234"
                value={search}
                onChange={(e) => setSearch(formatPlate(e.target.value))}
                className="pl-12 h-16 text-2xl font-bold tracking-widest uppercase text-center"
                autoFocus
              />
            </form>
            {search.length >= 7 && (
              <p className="text-destructive text-sm text-center animate-fade-in">
                Veículo não encontrado ou já finalizado.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  const price = calculatePrice(ticket, rules)
  const durationMins = differenceInMinutes(new Date(), ticket.checkInAt)

  const handlePayment = (method: string) => {
    checkOut(ticket.id, price, method)
    toast({
      title: 'Sucesso',
      description: `Saída de ${ticket.plate} finalizada.`,
    })
    navigate('/dashboard')
  }

  return (
    <div className="max-w-xl mx-auto md:mt-10 animate-fade-in-up pb-10">
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => setTicketId(null)}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
      </Button>
      <Card className="shadow-elevation overflow-hidden border-none md:border-solid">
        <div className="bg-primary p-6 text-primary-foreground text-center">
          <h2 className="text-4xl font-black tracking-widest flex items-center justify-center gap-2">
            {ticket.plate}
            {ticket.isMonthly && (
              <span className="bg-white/20 text-xs px-2 py-1 rounded-full font-bold uppercase tracking-normal">
                Mensalista
              </span>
            )}
          </h2>
          <p className="opacity-80 mt-1">
            {ticket.type} • Entrada às{' '}
            {ticket.checkInAt.toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
              timeZone: settings.timezone,
            })}
          </p>
        </div>
        <CardContent className="p-6 md:p-10 space-y-8">
          <div className="flex justify-between items-center border-b pb-6">
            <div className="text-muted-foreground">
              <p className="text-sm">Tempo de permanência</p>
              <p className="text-xl font-medium text-foreground">
                {Math.floor(durationMins / 60)}h {durationMins % 60}m
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Valor a cobrar</p>
              <p className="text-4xl font-bold text-secondary">
                {formatCurrency(price)}
              </p>
            </div>
          </div>

          {price > 0 ? (
            <div className="space-y-4">
              <h3 className="font-medium text-center text-muted-foreground">
                Selecione a forma de pagamento
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Button
                  onClick={() => handlePayment('Dinheiro')}
                  variant="outline"
                  className="h-20 flex flex-col gap-2 hover:border-primary hover:bg-primary/5"
                >
                  <Banknote className="h-6 w-6 text-emerald-600" /> Dinheiro
                </Button>
                <Button
                  onClick={() => handlePayment('Pix')}
                  variant="outline"
                  className="h-20 flex flex-col gap-2 hover:border-primary hover:bg-primary/5"
                >
                  <Smartphone className="h-6 w-6 text-teal-600" /> Pix
                </Button>
                <Button
                  onClick={() => handlePayment('Cartão')}
                  variant="outline"
                  className="h-20 flex flex-col gap-2 hover:border-primary hover:bg-primary/5"
                >
                  <CreditCard className="h-6 w-6 text-blue-600" /> Cartão
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50 text-emerald-800 p-4 rounded-lg border border-emerald-200 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 flex-shrink-0" />
              <div>
                {ticket.isMonthly
                  ? 'Veículo Mensalista. Nenhuma cobrança avulsa necessária.'
                  : 'Veículo dentro do tempo de tolerância ou isento. Nenhuma cobrança necessária.'}
              </div>
            </div>
          )}
        </CardContent>
        {price === 0 && (
          <CardFooter className="bg-muted p-6">
            <Button
              onClick={() =>
                handlePayment(ticket.isMonthly ? 'Mensalista' : 'Isento')
              }
              className="w-full btn-mobile-large font-bold"
            >
              {ticket.isMonthly
                ? 'Liberar Veículo (Mensalista)'
                : 'Liberar Veículo (Isento)'}
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  )
}
